import { useCallback, useSyncExternalStore } from 'react'

/**
 * Below this width the editor is one canvas with a sheet under it instead of
 * three columns: phones either way up, tablets held upright, narrow windows.
 * `editor.css` breaks at the same widths — change both together.
 */
export const COMPACT_EDITOR = '(max-width: 1023px)'

/**
 * A phone on its side. A sheet from below would leave a strip of room, so the
 * sheet docks to the side and the panel buttons move into the top bar.
 */
export const SHORT_EDITOR = '(max-width: 1023px) and (max-height: 520px)'

/**
 * A finger rather than a mouse: hints say "tap", and opening an empty chair
 * does not throw the keyboard over the room.
 */
export const TOUCH_FIRST = '(hover: none) and (pointer: coarse)'

/**
 * One `MediaQueryList` per query for the page's life. The editor re-renders on
 * every frame of a drag, and parsing a query — or re-subscribing to a new list —
 * on each of those frames is work a 120-table wedding notices.
 */
const lists = new Map<string, MediaQueryList>()

function listFor(query: string): MediaQueryList {
  let list = lists.get(query)
  if (!list) {
    list = window.matchMedia(query)
    lists.set(query, list)
  }
  return list
}

export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = listFor(query)
      list.addEventListener('change', onChange)
      return () => list.removeEventListener('change', onChange)
    },
    [query],
  )
  return useSyncExternalStore(subscribe, () => listFor(query).matches, () => false)
}

/** For event handlers, which need the answer now rather than on the next render. */
export function matchesMedia(query: string): boolean {
  return typeof window !== 'undefined' && listFor(query).matches
}
